import { useState } from 'react';
import api from '../services/api';

const ENTIDADES = ['Instrument', 'Calibration', 'User', 'Tenant'];
const OPERACOES = ['criar', 'editar', 'excluir'];

const COR_OPERACAO = {
  criar:   { bg: 'bg-emerald-950/40 border-emerald-800/40', texto: 'text-emerald-400', rotulo: 'Criar' },
  editar:  { bg: 'bg-blue-950/40 border-blue-800/40',       texto: 'text-blue-400',    rotulo: 'Editar' },
  excluir: { bg: 'bg-red-950/40 border-red-800/40',         texto: 'text-red-400',     rotulo: 'Excluir' },
};

const COR_ENTIDADE = {
  Instrument:  'text-indigo-400',
  Calibration: 'text-amber-400',
  User:        'text-slate-400',
  Tenant:      'text-purple-400',
};

function dataHora(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('pt-BR');
}

function BadgeOp({ operacao }) {
  const cfg = COR_OPERACAO[operacao] || { bg: 'bg-slate-800 border-slate-700', texto: 'text-slate-400', rotulo: operacao };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.texto}`}>
      {cfg.rotulo}
    </span>
  );
}

export default function AuditPage() {
  const [filtros, setFiltros] = useState({ entidade: '', operacao: '', de: '', ate: '' });
  const [dados, setDados] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState('');
  const [expandido, setExpandido] = useState(null);
  const LIMITE = 50;

  const setFiltro = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));

  const buscar = async (pag = 1) => {
    setCarregando(true);
    setErro('');
    setExpandido(null);
    try {
      const params = { pagina: pag, limite: LIMITE };
      if (filtros.entidade) params.entidade = filtros.entidade;
      if (filtros.operacao) params.operacao = filtros.operacao;
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;
      const { data } = await api.get('/audit', { params });
      setDados(data.dados);
      setTotal(data.total);
      setPagina(data.pagina);
      setTotalPaginas(data.totalPaginas);
    } catch (e) {
      setErro(e.response?.data?.mensagem || 'Erro ao carregar audit trail');
    } finally {
      setCarregando(false);
    }
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const params = {};
      if (filtros.entidade) params.entidade = filtros.entidade;
      if (filtros.operacao) params.operacao = filtros.operacao;
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;
      const { data } = await api.get('/audit/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_trail_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setErro('Erro ao exportar. Tente novamente.');
    } finally {
      setExportando(false);
    }
  };

  const toggleExpandido = (id) => setExpandido(prev => (prev === id ? null : id));

  return (
    <div className="flex flex-col h-full p-8">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-slate-200">Audit Trail</h1>
        <p className="text-xs text-slate-500 mt-0.5">Histórico completo de operações do sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl px-5 py-4 mb-4 flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Entidade</div>
          <select
            value={filtros.entidade}
            onChange={e => setFiltro('entidade', e.target.value)}
            className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600"
          >
            <option value="">Todas</option>
            {ENTIDADES.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>

        <div>
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Operação</div>
          <select
            value={filtros.operacao}
            onChange={e => setFiltro('operacao', e.target.value)}
            className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600"
          >
            <option value="">Todas</option>
            {OPERACOES.map(o => <option key={o} value={o}>{COR_OPERACAO[o]?.rotulo || o}</option>)}
          </select>
        </div>

        <div>
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">De</div>
          <input type="date" value={filtros.de} onChange={e => setFiltro('de', e.target.value)}
            className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
        </div>

        <div>
          <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Até</div>
          <input type="date" value={filtros.ate} onChange={e => setFiltro('ate', e.target.value)}
            className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
        </div>

        <button
          onClick={() => buscar(1)}
          disabled={carregando}
          className="px-4 py-1.5 text-xs font-semibold bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {carregando ? 'Buscando...' : 'Filtrar'}
        </button>

        <button
          onClick={exportar}
          disabled={exportando}
          className="px-4 py-1.5 text-xs font-semibold bg-emerald-800 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {exportando ? 'Exportando...' : '↓ Excel'}
        </button>

        {total > 0 && (
          <span className="ml-auto text-xs text-slate-500">{total} registro(s)</span>
        )}
      </div>

      {erro && <p className="text-xs text-red-400 mb-3">{erro}</p>}

      {/* Tabela */}
      <div className="flex-1 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden flex flex-col min-h-0">
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--surface-2)] z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-600 border-b border-[var(--surface-4)]">
                <th className="text-left px-4 py-3 font-medium w-36">Data / Hora</th>
                <th className="text-left px-4 py-3 font-medium">Usuário</th>
                <th className="text-left px-4 py-3 font-medium w-20">Operação</th>
                <th className="text-left px-4 py-3 font-medium w-24">Entidade</th>
                <th className="text-left px-4 py-3 font-medium">ID</th>
                <th className="text-left px-4 py-3 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {!carregando && dados.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-600">
                    {total === 0 && !erro ? 'Use os filtros acima para buscar registros' : 'Nenhum registro encontrado'}
                  </td>
                </tr>
              )}
              {carregando && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-600">Carregando...</td>
                </tr>
              )}
              {!carregando && dados.map((r) => (
                <>
                  <tr
                    key={r._id}
                    onClick={() => r.camposAlterados?.length > 0 && toggleExpandido(r._id)}
                    className={`border-b border-[var(--surface-4)]/50 transition-colors ${
                      r.camposAlterados?.length > 0 ? 'cursor-pointer hover:bg-white/[0.02]' : ''
                    }`}
                  >
                    <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{dataHora(r.timestamp)}</td>
                    <td className="px-4 py-2.5 text-slate-300">{r.userName}</td>
                    <td className="px-4 py-2.5"><BadgeOp operacao={r.operacao} /></td>
                    <td className={`px-4 py-2.5 font-medium ${COR_ENTIDADE[r.entidade] || 'text-slate-400'}`}>
                      {r.entidade}
                    </td>
                    <td className="px-4 py-2.5 text-[11px]">
                      {r.entidadeLabel
                        ? <span className="font-mono font-semibold text-slate-300">{r.entidadeLabel}</span>
                        : <span className="font-mono text-slate-600 text-[10px]">{r.entidadeId}</span>
                      }
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {r.camposAlterados?.length > 0 && (
                        <span className="text-[10px]">{expandido === r._id ? '▲' : '▼'}</span>
                      )}
                    </td>
                  </tr>
                  {expandido === r._id && r.camposAlterados?.length > 0 && (
                    <tr key={`${r._id}-exp`} className="border-b border-[var(--surface-4)]/50 bg-[var(--surface-1)]/40">
                      <td colSpan={6} className="px-6 py-3">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Campos alterados</div>
                        <div className="space-y-1">
                          {r.camposAlterados.map((c, i) => (
                            <div key={i} className="flex items-start gap-3 text-xs">
                              <span className="text-slate-500 w-32 flex-shrink-0 font-mono">{c.campo}</span>
                              <span className="text-red-400/70 line-through max-w-xs truncate">
                                {c.valorAntes != null ? String(c.valorAntes) : '—'}
                              </span>
                              <span className="text-slate-500">→</span>
                              <span className="text-emerald-400/80 max-w-xs truncate">
                                {c.valorDepois != null ? String(c.valorDepois) : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--surface-4)] flex-shrink-0">
            <span className="text-xs text-slate-600">
              Página {pagina} de {totalPaginas}
            </span>
            <div className="flex gap-2">
              <button
                disabled={pagina <= 1}
                onClick={() => buscar(pagina - 1)}
                className="px-3 py-1 text-xs text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg disabled:opacity-30 transition-colors"
              >
                ← Anterior
              </button>
              <button
                disabled={pagina >= totalPaginas}
                onClick={() => buscar(pagina + 1)}
                className="px-3 py-1 text-xs text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] rounded-lg disabled:opacity-30 transition-colors"
              >
                Próxima →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
